/**
 * FirstPromoter referral code (fpr) storage utility.
 * Captures fpr from URL query string and persists in localStorage.
 *
 * Used by:
 * - main.tsx: auto-capture on app load
 * - authStore: read on login/signup success to auto-bind
 * - PlanDetailsPage: read to decide whether to show input UI
 */

const FPR_KEY = 'yepai_fpr';
const FPR_EXPIRY_DAYS = 90;
const EXPIRY_MS = FPR_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

interface StoredFpr {
  value: string;
  expiresAt: number;
}

/**
 * Capture fpr from current URL query string and save to localStorage.
 * Idempotent — does nothing if URL has no fpr param.
 */
export function captureFprFromUrl(): string | null {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const fpr = urlParams.get('fpr');
    if (!fpr || fpr.trim().length === 0) {
      return null;
    }
    saveFpr(fpr);
    console.log(`[referralStorage] Captured fpr from URL: ${fpr}`);
    return fpr;
  } catch (error) {
    console.error('[referralStorage] captureFprFromUrl failed:', error);
    return null;
  }
}

/**
 * Save fpr to localStorage with 90-day expiry.
 */
export function saveFpr(fpr: string): void {
  try {
    const data: StoredFpr = {
      value: fpr,
      expiresAt: Date.now() + EXPIRY_MS,
    };
    localStorage.setItem(FPR_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('[referralStorage] saveFpr failed:', error);
  }
}

/**
 * Read fpr from localStorage. Returns null if missing or expired.
 */
export function getFpr(): string | null {
  try {
    const raw = localStorage.getItem(FPR_KEY);
    if (!raw) return null;
    const data: StoredFpr = JSON.parse(raw);
    if (Date.now() > data.expiresAt) {
      clearFpr();
      return null;
    }
    return data.value;
  } catch (error) {
    console.error('[referralStorage] getFpr failed:', error);
    return null;
  }
}

/**
 * Remove stored fpr — called after successful binding.
 */
export function clearFpr(): void {
  try {
    localStorage.removeItem(FPR_KEY);
  } catch (error) {
    console.error('[referralStorage] clearFpr failed:', error);
  }
}

/**
 * Check if a fpr currently exists in storage.
 */
export function hasFpr(): boolean {
  return getFpr() !== null;
}
